import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { getThemeColor, resolveThemeScheme } from '@/lib/theme';
import { readStorage, storageKeys, writeStorage } from '@/lib/storage';
import type { ThemeMode } from '@/lib/types';

type ThemeContextValue = {
  themeMode: ThemeMode;
  resolvedScheme: 'light' | 'dark';
  isReady: boolean;
  setThemeMode: (value: ThemeMode) => Promise<void>;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

function getSystemScheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') {
    return 'dark';
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themeMode, setThemeModeState] = useState<ThemeMode>('system');
  const [preferredScheme, setPreferredScheme] = useState<'light' | 'dark'>(getSystemScheme());
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const storedMode = readStorage(storageKeys.themeMode);

    if (storedMode === 'system' || storedMode === 'light' || storedMode === 'dark') {
      setThemeModeState(storedMode);
    }

    setIsReady(true);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      setPreferredScheme(media.matches ? 'dark' : 'light');
    };

    handleChange();
    media.addEventListener('change', handleChange);

    return () => {
      media.removeEventListener('change', handleChange);
    };
  }, []);

  const resolvedScheme: 'light' | 'dark' = resolveThemeScheme(themeMode, preferredScheme);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    document.documentElement.dataset.theme = resolvedScheme;
    document.documentElement.style.colorScheme = resolvedScheme;

    const metaThemeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');

    if (metaThemeColor) {
      metaThemeColor.content = getThemeColor(themeMode, preferredScheme);
    }
  }, [preferredScheme, resolvedScheme, themeMode]);

  const setThemeMode = useCallback(async (value: ThemeMode) => {
    setThemeModeState(value);
    writeStorage(storageKeys.themeMode, value);
  }, []);

  const value = useMemo(
    () => ({ themeMode, resolvedScheme, isReady, setThemeMode }),
    [isReady, resolvedScheme, setThemeMode, themeMode]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useThemeState() {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useThemeState must be used within a ThemeProvider');
  }

  return context;
}
